import React from 'react';
// components
import { FormattedMessage } from 'react-intl';
import FormattedWeight from 'components/FormattedWeight';
// utils
import { formatDate } from './formatDateHelper';
import { getAverage } from './index';
import { formatBackEndWeightToSystem } from './weightHelper';
// types
import {
  LoadCommitment,
  MeasurementSystemType,
  SalesResult,
  SalesResultFormValues,
  TruckingLoadCommitment,
} from 'types';

export function buildPreFilledSalesResults(
  sources: LoadCommitment[] | TruckingLoadCommitment[],
  destinations: LoadCommitment[] | TruckingLoadCommitment[]
) {
  const salesResults: SalesResult[] = [];
  sources.forEach((source: LoadCommitment | TruckingLoadCommitment) => {
    destinations.forEach((dest: LoadCommitment | TruckingLoadCommitment) => {
      salesResults.push({
        id: '',
        source_loading_commitment_id: +source.id,
        destination_loading_commitment_id: +dest.id,
        delivered_at: dest.arrive_at || '',
        head_count: source.head_count || 0,
        dead_on_arrival: 0,
        coefficient_of_variation: '',
        data_source: '',
        data_recorded_at: '',
        avg_pig_weight: (
          source.pig_group?.estimated_weight ||
          getAverage(dest.contract_commitment?.weight_min, dest.contract_commitment?.weight_max)
        ).toString(),
        comment: '',
      });
    });
  });
  return salesResults;
}

export function getInitialSalesResults(
  salesResults: SalesResult[],
  measurementSystem: MeasurementSystemType
): SalesResultFormValues[] {
  return salesResults.map(salesResult => ({
    id: salesResult.id,
    avg_pig_weight: salesResult.avg_pig_weight
      ? formatBackEndWeightToSystem(
          parseFloat(salesResult.avg_pig_weight),
          measurementSystem
        ).toString()
      : '0',
    arrive_hour: +formatDate(salesResult.delivered_at, 'HH'),
    arrive_minute: +formatDate(salesResult.delivered_at, 'mm'),
    comment: salesResult.comment || '',
    delivered_at: formatDate(salesResult.delivered_at),
    head_count: salesResult.head_count || 0,
    modified_by: salesResult.modified_by,
    source_loading_commitment_id: salesResult.source_loading_commitment_id,
    destination_loading_commitment_id: salesResult.destination_loading_commitment_id,
    locked: salesResult.locked,
    dead_on_arrival: salesResult.dead_on_arrival || 0,
    coefficient_of_variation: salesResult.coefficient_of_variation || '',
    data_source: salesResult.data_source || '',
    data_recorded_at: salesResult.data_recorded_at || '',
  }));
}

export function getSalesResultInfo(
  salesResult: SalesResult,
  destCommitment?: TruckingLoadCommitment
) {
  const contractHeadCount = destCommitment?.contract_commitment?.head_count;
  return [
    {
      label: <FormattedMessage id="load.salesResults.dateDelivered" />,
      value: salesResult?.delivered_at ? formatDate(salesResult.delivered_at) : 'N/A',
      targetValue: destCommitment?.arrive_at ? formatDate(destCommitment.arrive_at) : 'N/A',
      focus: 'delivered_date',
      step: 'delivered_datetime',
    },
    {
      label: <FormattedMessage id="load.salesResults.deliveryTime" />,
      value: salesResult?.delivered_at ? formatDate(salesResult.delivered_at, 't') : 'N/A',
      targetValue: destCommitment?.arrive_at ? formatDate(destCommitment.arrive_at, 't') : 'N/A',
      focus: 'delivered_time',
      step: 'delivered_datetime',
    },
    {
      label: <FormattedMessage id="load.salesResults.headDelivered" />,
      value: salesResult?.head_count || 'N/A',
      targetValue: contractHeadCount || 'N/A',
      focus: 'head_count',
      step: 'delivered_head',
    },
    {
      label: <FormattedMessage id="load.salesResults.prelimAvgWeight" />,
      value: <FormattedWeight value={salesResult?.avg_pig_weight} />,
      targetValue: <FormattedWeight value={salesResult?.avg_pig_weight} />,
      focus: 'avg_pig_weight',
      step: 'avg_pig_weight',
    },
  ];
}
